import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import { Input } from '@shadcn/components/ui/input';
import Select from '@shadcn/components/ui/react-select';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';
import { Calendar, Filter, X } from 'lucide-react';
import * as moment from 'moment';

const WeeklySalesFilterModal = ({ 
  isOpen, 
  onClose, 
  filters, 
  onApplyFilters,
  regionConfigOptions 
}) => {
  const [tempFilters, setTempFilters] = useState(filters);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch products data from API
  const fetchProductsData = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .get(`${backendUrl}/product/active-skus`)
        .query({ region: 'AU' }) // Default region for products
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      if (response.body && Array.isArray(response.body)) {
        setProducts(response.body);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTempFilters(filters);
      fetchProductsData();
    }
  }, [isOpen, filters]);

  // Generate category options
  const categoryOptions = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean);
    return uniqueCategories.sort().map(category => ({
      value: category,
      label: category
    }));
  }, [products]);

  // Generate product options (filtered by selected categories)
  const productOptions = useMemo(() => {
    let filteredProducts = products;
    
    if (tempFilters.categoryNames && tempFilters.categoryNames.length > 0) {
      filteredProducts = products.filter(product => 
        tempFilters.categoryNames.includes(product.category)
      );
    }
    
    // Group by productName to avoid duplicates
    const productMap = new Map();
    filteredProducts.forEach(item => {
      if (!productMap.has(item.productName)) {
        productMap.set(item.productName, {
          _id: item._id,
          productName: item.productName,
          category: item.category,
          skus: [item.sku]
        });
      } else {
        productMap.get(item.productName).skus.push(item.sku);
      }
    });
    
    return Array.from(productMap.values()).map(product => ({
      value: product._id,
      label: product.productName,
      category: product.category
    }));
  }, [products, tempFilters.categoryNames]);


  const handleDateChange = (field, value) => {
    const date = new Date(value);
    if (field === 'startDate') {
      const alignedStart = moment(date).tz('utc').startOf('isoWeek').toDate();
      setTempFilters(prev => ({
        ...prev,
        startDate: alignedStart
      }));
    } else if (field === 'endDate') {
      const alignedEnd = moment(date).tz('utc').endOf('isoWeek').toDate();
      setTempFilters(prev => ({
        ...prev,
        endDate: alignedEnd
      }));
    }
  };

  const handleCategoryChange = (selectedCategories) => {
    const categoryNames = selectedCategories ? selectedCategories.map(s => s.value) : [];
    setTempFilters(prev => ({
      ...prev,
      categoryNames,
      // Clear products if they don't belong to selected categories
      productIds: prev.productIds?.filter(productId => {
        const product = products.find(p => p._id === productId);
        return product && categoryNames.includes(product.category);
      }) || []
    }));
  };

  const handleProductChange = (selectedProducts) => {
    const productNames = selectedProducts ? selectedProducts.map(s => s.label) : [];
    
    const productIds = selectedProducts ? selectedProducts.map(s => s.value) : [];
    setTempFilters(prev => {
    
      const selectedCategories = [...new Set(selectedProducts?.map(p => p.category) || [])];
      const currentCategories = new Set(prev.categoryNames || []);
      selectedCategories.forEach(category => currentCategories.add(category));
      
      return {
        ...prev,
        productNames,
        productIds,
        categoryNames: Array.from(currentCategories)
      };
    });
  };

  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

      const handleClear = () => {
      const clearedFilters = {
        ...tempFilters,
        categoryNames: [],
        productNames: [],
        productIds: []
      };
      setTempFilters(clearedFilters);
    };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(tempFilters) !== JSON.stringify(filters);
  }, [tempFilters, filters]);

      const activeFiltersCount = (tempFilters.categoryNames?.length || 0) + (tempFilters.productIds?.length || 0);

  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Weekly Sales Filters
            {activeFiltersCount > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Range Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date Range (Week Aligned)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formatDateForInput(tempFilters.startDate)}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                />
                <p className="text-xs text-gray-500">Will align to start of week (Monday)</p>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formatDateForInput(tempFilters.endDate)}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                />
                <p className="text-xs text-gray-500">Will align to end of week (Sunday)</p>
              </div>
            </div>
          </div>

          {/* Region Config Section */}
          <div className="space-y-2">
            <Label>Region Configs</Label>
            <Select
              isMulti
              value={regionConfigOptions.filter(option => 
                tempFilters.regionConfigs?.includes(option.value)
              )}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                setTempFilters(prev => ({
                  ...prev,
                  regionConfigs: selectedValues
                }));
              }}
              options={regionConfigOptions}
              placeholder="Select region configs..."
              className="text-sm"
              classNamePrefix="react-select"
              maxMenuHeight={200}
            />
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <Label>Categories</Label>
            <Select
              isMulti
              value={categoryOptions.filter(opt => tempFilters.categoryNames?.includes(opt.value))}
              onChange={handleCategoryChange}
              options={categoryOptions}
              placeholder="Select categories..."
              className="text-sm"
              classNamePrefix="react-select"
              maxMenuHeight={200}
              isLoading={loading}
            />
            {tempFilters.categoryNames?.length > 0 && (
              <p className="text-xs text-gray-500">
                {tempFilters.categoryNames.length} categor{tempFilters.categoryNames.length !== 1 ? 'ies' : 'y'} selected
              </p>
            )}
          </div>

          {/* Product Filter */}
          <div className="space-y-2">
            <Label>Products</Label>
            <Select
              isMulti
              value={productOptions.filter(opt => tempFilters.productIds?.includes(opt.value))}
              onChange={handleProductChange}
              options={productOptions}
              placeholder="Select products..."
              className="text-sm"
              classNamePrefix="react-select"
              maxMenuHeight={200}
              isLoading={loading}
            />
                         {tempFilters.productIds?.length > 0 && (
               <p className="text-xs text-gray-500">
                 {tempFilters.productIds.length} product{tempFilters.productIds.length !== 1 ? 's' : ''} selected
               </p>
             )}
          </div>

          {/* Active Filters Preview */}
          {activeFiltersCount > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-gray-700">Active Filters:</h5>
                <Button 
                  onClick={handleClear}
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear All
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tempFilters.categoryNames?.map(categoryName => (
                  <span
                    key={categoryName}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                  >
                    📂 {categoryName}
                  </span>
                ))}
                                 {tempFilters.productIds?.map(productId => {
                   const product = products.find(p => p._id === productId);
                   return product ? (
                     <span
                       key={productId}
                       className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                     >
                       📦 {product.productName}
                     </span>
                   ) : null;
                 })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={!hasChanges}
          >
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklySalesFilterModal;
